import { useClientPortalAPI } from "@certn/api";
import { FormLabelRAC, InputButton, Picker, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Key, useRef } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  onDone: (selectedGroups: Key[]) => void;
  userGroups: string[];
}

export function UserGroupsSelect({ onDone, userGroups }: Props) {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const { data: groupOptions } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/customer-groups-for-user-management/`
  );
  const filteredOptions =
    groupOptions?.filter((group) => !userGroups.includes(group.id)) || [];

  return (
    <Picker
      showActionBar
      triggerRef={triggerRef as React.RefObject<HTMLDivElement>}
      maxWidth="560px"
      items={filteredOptions.map(({ id, name, user_count: userCount }) => ({
        id,
        name,
        subtitle:
          userCount === 1
            ? t("1 member")
            : t("{{userCount}} members", { userCount }),
      }))}
      label={t("Select group(s)")}
      selectionMode="multiple"
      testID="group-picker"
      onDone={onDone}
      actionText={t("Add")}
      trigger={(props) => (
        <div ref={triggerRef} className={styles.triggerContainer}>
          <FormLabelRAC isRequired>{props.label}</FormLabelRAC>
          <InputButton
            data-testid={props.testID}
            rightIcon="expand_more"
            isDisabled={!filteredOptions.length}
          >
            <Text
              style="input/text-field"
              color={
                props.selectedItems.length < 1
                  ? "text-placeholder"
                  : "text-base"
              }
            >
              {props.selectedItems[0]
                ? props.selectedItems[0].name
                : t("Select groups")}
              {props.selectedItems.length > 1 &&
                ` +${props.selectedItems.length - 1}`}
            </Text>
          </InputButton>
        </div>
      )}
    />
  );
}

const styles = {
  triggerContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1-5)",
  }),
};
